import React, { useEffect, useRef, useState } from 'react';
import { Pen, Type, Eraser, Check } from 'lucide-react';
import { useConsoleTheme } from '../../lib/theme';

// SignaturePanel — Sprint D item 3.2 portal-side signature capture.
//
// Two modes: typed (4 cursive Google Fonts) + drawn (HTML5 canvas). Submit
// button locked until ALL three legal checkboxes are ticked AND a signature
// payload exists. The 4 typed-mode fonts are loaded via Google Fonts CDN
// (one <link> tag injected once per page).
//
// Submit handler is provided by the caller and receives:
//   { signature_type, signature_data, font_family, legal_acknowledged }
// Caller is responsible for the API call + state updates afterwards.

const TYPED_FONTS = [
  { id: 'Dancing Script', label: 'Dancing Script' },
  { id: 'Caveat',         label: 'Caveat' },
  { id: 'Allura',         label: 'Allura' },
  { id: 'Great Vibes',    label: 'Great Vibes' },
];

// Ink of the typed preview and of the drawn strokes. Dark mode keeps its white;
// light mode (docs/light-mode.md §3) uses ink so the signature shows on paper.
const SIGNATURE_INK = { dark: '#FFFFFF', light: '#1C1B16' };

const FONTS_HREF =
  'https://fonts.googleapis.com/css2?' +
  TYPED_FONTS.map((f) => `family=${encodeURIComponent(f.id)}:wght@400`).join('&') +
  '&display=swap';

let fontsInjected = false;
function ensureFontsLoaded() {
  if (fontsInjected || typeof document === 'undefined') return;
  if (document.querySelector('link[data-signature-fonts]')) {
    fontsInjected = true;
    return;
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = FONTS_HREF;
  link.setAttribute('data-signature-fonts', '1');
  document.head.appendChild(link);
  fontsInjected = true;
}

export default function SignaturePanel({ defaultName = '', onSubmit, submitting = false }) {
  const [mode, setMode] = useState('typed');
  const [typedName, setTypedName] = useState(defaultName);
  const [fontFamily, setFontFamily] = useState(TYPED_FONTS[0].id);
  const [drawnPng, setDrawnPng] = useState('');
  const [legal, setLegal] = useState({ accuracy: false, authority: false, terms: false });
  const [error, setError] = useState('');

  useEffect(() => { ensureFontsLoaded(); }, []);

  const allLegalChecked = legal.accuracy && legal.authority && legal.terms;
  const hasSignaturePayload =
    (mode === 'typed' && typedName.trim().length > 0) ||
    (mode === 'drawn' && drawnPng.length > 0);
  const canSubmit = allLegalChecked && hasSignaturePayload && !submitting;

  const handleSubmit = async () => {
    setError('');
    if (!canSubmit) return;
    try {
      await onSubmit?.({
        signature_type: mode,
        signature_data: mode === 'typed' ? typedName.trim() : drawnPng,
        font_family: mode === 'typed' ? fontFamily : null,
        legal_acknowledged: { ...legal },
      });
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Failed to submit signature');
    }
  };

  return (
    <div data-testid="signature-panel" className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 space-y-5">
      <div>
        <div className="text-sm font-semibold text-white">Sign this invoice</div>
        <div className="text-[12px] text-gray-400 mt-1">
          Once submitted, your signature locks the invoice. You can save a signed PDF afterward.
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="signature-mode-typed"
          onClick={() => setMode('typed')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
            mode === 'typed'
              ? 'bg-[#B0FC38]/[0.15] text-[#B0FC38] border-[#B0FC38]/40'
              : 'border-white/[0.08] text-gray-400 hover:text-white'
          }`}
        >
          <Type className="w-3.5 h-3.5" /> Typed
        </button>
        <button
          type="button"
          data-testid="signature-mode-drawn"
          onClick={() => setMode('drawn')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
            mode === 'drawn'
              ? 'bg-[#B0FC38]/[0.15] text-[#B0FC38] border-[#B0FC38]/40'
              : 'border-white/[0.08] text-gray-400 hover:text-white'
          }`}
        >
          <Pen className="w-3.5 h-3.5" /> Drawn
        </button>
      </div>

      {mode === 'typed' ? (
        <TypedMode
          name={typedName}
          onName={setTypedName}
          fontFamily={fontFamily}
          onFontFamily={setFontFamily}
        />
      ) : (
        <DrawnMode dataUrl={drawnPng} onDataUrl={setDrawnPng} />
      )}

      <fieldset className="space-y-2">
        <legend className="text-[11px] uppercase tracking-wider text-gray-400/80">Legal acknowledgements</legend>
        <LegalCheckbox
          testId="legal-accuracy"
          checked={legal.accuracy}
          onChange={(v) => setLegal({ ...legal, accuracy: v })}
          label="I confirm the information on this invoice is accurate."
        />
        <LegalCheckbox
          testId="legal-authority"
          checked={legal.authority}
          onChange={(v) => setLegal({ ...legal, authority: v })}
          label="I have authority to sign on behalf of the school."
        />
        <LegalCheckbox
          testId="legal-terms"
          checked={legal.terms}
          onChange={(v) => setLegal({ ...legal, terms: v })}
          label="I agree to the NexSav service terms referenced above."
        />
      </fieldset>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          data-testid="signature-submit"
          disabled={!canSubmit}
          onClick={handleSubmit}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#B0FC38]/20"
        >
          <Check className="w-4 h-4" />
          {submitting ? 'Submitting…' : 'Sign invoice'}
        </button>
      </div>
    </div>
  );
}

function TypedMode({ name, onName, fontFamily, onFontFamily }) {
  const { isLight } = useConsoleTheme();
  const ink = isLight ? SIGNATURE_INK.light : SIGNATURE_INK.dark;
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5">Your full name</span>
        <input
          type="text"
          data-testid="signature-typed-name"
          value={name}
          onChange={(e) => onName(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white"
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        {TYPED_FONTS.map((f) => (
          <button
            type="button"
            key={f.id}
            data-testid={`signature-font-${f.id.replace(/\s+/g, '-')}`}
            onClick={() => onFontFamily(f.id)}
            className={`text-left px-3 py-3 rounded-lg border transition-colors ${
              fontFamily === f.id
                ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.06]'
                : 'border-white/[0.08] hover:border-white/[0.15]'
            }`}
          >
            <span style={{ fontFamily: `'${f.id}', cursive`, fontSize: 24, color: isLight ? ink : 'white' }}>
              {name.trim() || f.label}
            </span>
            <div className="text-[10px] text-gray-500 mt-1">{f.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function DrawnMode({ dataUrl, onDataUrl }) {
  const { isLight } = useConsoleTheme();
  const ink = isLight ? SIGNATURE_INK.light : SIGNATURE_INK.dark;
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const lastPos = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth * (window.devicePixelRatio || 1);
    canvas.height = canvas.offsetHeight * (window.devicePixelRatio || 1);
    // JSDOM (used by jest) doesn't implement getContext; wrap in try so
    // unit tests that exercise the panel without rendering pixels still pass.
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = ink;
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: (t?.clientX ?? e.clientX) - r.left,
      y: (t?.clientY ?? e.clientY) - r.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    lastPos.current = getPos(e);
  };
  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    // Follow a theme switch made while the pad is open (new strokes only).
    ctx.strokeStyle = ink;
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPos.current = p;
  };
  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onDataUrl(dataUrl);
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    onDataUrl('');
  };

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-white/[0.08] bg-white/[0.02]">
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          className="block w-full h-40 touch-none"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>Draw your signature in the box above.</span>
        <button
          type="button"
          data-testid="signature-clear"
          onClick={clear}
          className="inline-flex items-center gap-1 hover:text-white"
        >
          <Eraser className="w-3 h-3" /> Clear
        </button>
      </div>
      {dataUrl && <input type="hidden" data-testid="signature-payload" value={dataUrl} readOnly />}
    </div>
  );
}

function LegalCheckbox({ testId, checked, onChange, label }) {
  return (
    <label className="flex items-start gap-2 text-[13px] text-gray-300 cursor-pointer">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
      <span>{label}</span>
    </label>
  );
}
